import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  ReplaySubject,
  skipWhile,
  Subscription,
  take
} from 'rxjs';
import { ScenarioImage } from '../models/scenario-image';
import { LoadingComponent } from '../models/loading.component';
import { ScenarioImageService } from '../services/scenario-image/scenario-image.service';
import { ApplicationStateService } from '../services/application.state.service';
import { QrCodeService } from '../services/qr-code/qr-code.service';
import { TrackingService } from '../services/tracking.service';
import { preloadImages } from '../utils/image-preload';
import { Size } from '../models/utils';
import { Router } from '@angular/router';
import { DoorService } from '../services/door/door.service';
import { CustomerService } from '../services/customer/customer.service';
import { DeviceType } from '../../generated/graphql';
import { SafeUrl } from '@angular/platform-browser';
import {DoorTypeValidationService} from "../services/doorTypeValidation/door-type-validation.service";

type Props = {
  images: Array<ScenarioImage>;
  selected: ScenarioImage;
}

@Component({ template: '' })
export abstract class AbstractScenarioPanelComponent implements OnInit, OnDestroy, LoadingComponent {

  subscriptions: Array<Subscription> = [];

  scenarios$ = this.scenarioImageService.getScenarios$();
  firstExampleImage$ = this.scenarios$.pipe(map(scenarios => scenarios.find(s => s.type === 'EXAMPLE')?.thumbnailUrl));
  props$: Observable<Props> = combineLatest([
    this.scenarios$,
    this.scenarioImageService.getSelected$()
  ]).pipe(map(([images, selected]) => ({ images, selected })));
  showScenariosLoadingIndicator$ = this.scenarioImageService.isLoading$.pipe(distinctUntilChanged(), debounceTime(300));

  windowSize$ = new ReplaySubject<Size>(1);
  isLoaded$ = new BehaviorSubject(false);
  showFlap = false;
  showMore = false;

  qrCodeColorLight$ = new BehaviorSubject('#ffffff');
  qrCodeColorDark$ = new BehaviorSubject('#000000');

  ngOnInit(): void {
    this.subscriptions.push(
      this.customerService.getCustomerConfig$().subscribe({
        next: config => {

          if (config.colors.uploadPanel.background) {
            this.qrCodeColorLight$.next(config.colors.uploadPanel.background);
          }
          if (config.colors.uploadPanel.color) {
            this.qrCodeColorDark$.next(config.colors.uploadPanel.color);
          }
        }
      }));

    // subscribe to scenario images and trigger preload of thumbnails
    this.subscriptions.push(
      this.scenarioImageService.getScenarios$().pipe(
        skipWhile(images => images.length === 0),
        take(1)
      ).subscribe({
        next: images => {
          preloadImages(images.map(i => i.thumbnailUrl)).subscribe({
            next: v => this.isLoaded$.next(v),
            error: (error) => {
              this.router.navigateByUrl('error').then();
            }
          });
        }
      })
    );

    this.subscriptions.push(combineLatest([this.windowSize$, this.scenarioImageService.getScenarios$()]).subscribe(([windowSize, images]) => {
      if (windowSize.width >= 1400) {
        this.showFlap = images.length > 6;
      } else if (windowSize.width >= 992) {
        this.showFlap = images.length > 4;
      } else {
        this.showFlap = images.length > 3;
      }
    }));
  }

  constructor(protected scenarioImageService: ScenarioImageService,
              protected doorService: DoorService,
              public applicationStateService: ApplicationStateService,
              protected trackingService: TrackingService,
              public qrCodeService: QrCodeService,
              protected router: Router,
              private customerService: CustomerService,
              private doorTypeValidationService: DoorTypeValidationService,
  ) {
    this.applicationStateService.register(this);
    this.onResize();
  }

  @HostListener('window:mousedown')
  onOutsideClick() {
    this.showMore = false;
  }

  @HostListener('window:resize',)
  async onResize(): Promise<void> {
    this.windowSize$.next({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }

  inContainerClick(event: Event) {
    event.stopPropagation();
  }

  onScenarioSelected(scenario: ScenarioImage) {
    this.trackingService.track({ event: 'selectScenario', body: { selectedScenarioId: scenario.id } }).then();

    this.scenarioImageService.selectScenario(scenario);
    this.showMore = false;
  }

  previewOfUploadedImage?: string | SafeUrl = undefined;

  onUploadFile(event: Event) {
    const element = event.target as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      const reader = new FileReader();

      reader.addEventListener('load', () => {
        this.previewOfUploadedImage = reader.result as string;

        this.scenarioImageService.uploadUserScenario({ file, deviceType: DeviceType.Desktop }).then(({ showRefineLayout }) => {
          if (showRefineLayout) {
            this.applicationStateService.setRefineMode('EDIT');
          } else {
            this.applicationStateService.setHasUploadedImage(true);
            this.applicationStateService.setRefineMode('PREVIEW');
          }

          this.previewOfUploadedImage = undefined;

          this.trackingService.track({ event: 'uploadScenario' }).then();
        });
      });
      reader.readAsDataURL(file);
      element.value = ''; // reset input so user can upload same file again
    }
  }

  deleteUserScenario(scenario: ScenarioImage): void {
    this.trackingService.track({ event: 'deleteScenario', body: { scenarioIdToDelete: scenario.id } }).then();
    this.scenarioImageService.deleteScenario(scenario.shortId);
  }

  isLoaded(): Observable<boolean> {
    return this.isLoaded$;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.applicationStateService.unregister(this);
  }

  // widget
  @ViewChild('scrollableContainer')
  scrollableContainer!: ElementRef;

  onScroll(event: WheelEvent): void {
    this.doScroll(event.deltaY);
  }

  doScroll(delta: number) {
    this.scrollableContainer.nativeElement.scrollBy({
      top: 0,
      left: delta,
      behavior: 'auto'
    });
  }

  // TODO: refactor to common utility?
  scrollToNextElement(dir: 1 | -1) {
    const scenarios: Element[] = [...this.scrollableContainer.nativeElement.getElementsByClassName('scenario')];
    const scrollX = this.scrollableContainer.nativeElement.scrollLeft;

    if (dir < 0) {
      scenarios.reverse()
    }

    for (let i = 0; i < scenarios.length; i++) {
      const scenario = scenarios[i] as HTMLDivElement;
      if (Math.sign(scenario.offsetLeft - scrollX) === dir) {
        this.scrollableContainer.nativeElement.scrollTo({
          top: 0,
          left: scenario.offsetLeft,
          behavior: 'auto'
        });
        break;
      }
    }
  }

  scrollLeft(): void {
    this.scrollToNextElement(-1);
  }

  scrollRight(): void {
    this.scrollToNextElement(1);
  }
}
